import { useSyncExternalStore } from 'react';
import { OUTBOX_KEEP_MS, RESEND_AFTER_ONLINE_MS, RESEND_BACKOFF_MS } from '@constants/timing';
import { api, ApiError, ERROR_CODE, errorText, send } from '@lib/api';
import { sessionStore } from '@lib/storage';
import { idbAllFor, idbDelete, idbPurgeOlder, idbPut, type StoredRecord } from '@lib/utils/idbStore';

/**
 * What the field could not send yet (an exchange at a house without signal, a full cylinder scanned while the line was
 * down, a failed delivery, a counter exchange), sent one at a time in the order recorded, so an order's "out for
 * delivery" always reaches the server before its exchange. Every send is idempotent (its token, the same scan again).
 *
 * - Kept per signed-in user: in memory, and in IndexedDB WITHOUT the citizen's confirmation code, so a closed or killed
 *   tab loses nothing but the code: back on this device, the same user re-enters it ("needsCode") and it goes.
 * - No connection, a server error, too many requests (its Retry-After honoured): tried again after a growing pause or
 *   when the connection returns. A 401: paused until the same user signs in again.
 * - A business refusal (a wrong code, a cylinder not available: 4xx with its code) stops that order's items and waits
 *   for the user to open the order; other orders go on. Photos travel as upload ids of photos already uploaded; one
 *   the server no longer holds (HUB_UPLOAD_INVALID) is dropped from the drafts and the photo is taken again. A resend refused as out of state is first checked against the
 *   order (`recorded`): an earlier send whose answer was lost counts as sent.
 * - Signing out (or another user signing in) clears what is in memory; another user's stored items stay invisible.
 *   Items older than 7 days are dropped. Closing the tab while anything waits asks first.
 */
export type OutboxKind = 'outForDelivery' | 'complete' | 'deliveryFailed' | 'completePickup' | 'counter';
export interface OutboxRequest { path: string; body: Record<string, unknown> }
export type OutboxState = 'waiting' | 'sending' | 'refused' | 'needsCode';
export interface OutboxItem {
  key: string;
  orderId: number;
  number: string;
  kind: OutboxKind;
  state: OutboxState;
  error: string | null;
  errorCode: string | null;
  queuedAt: number;
}
interface Stored extends StoredRecord {
  itemKey: string;
  orderId: number;
  number: string;
  kind: OutboxKind;
  /** the body without the confirmation code */
  request: OutboxRequest;
  /** the body had a code: after a restore it has to be entered again */
  hadCode: boolean;
  /** a resend refused as out of state: was it recorded? (the order's path, the statuses that say so) */
  recorded: { path: string; statuses: string[] } | null;
  /** tab drafts to clear once it is recorded */
  draftKeys: string[];
}
interface Entry extends Stored, OutboxItem {
  secret: { confirmationCode?: string } | null;
}
export interface OutboxEvent { item: OutboxItem; outcome: 'sent' | 'refused' }

let user: number | null = null;
let paused = false;
let entries: Entry[] = [];
let snapshot: readonly OutboxItem[] = [];
let failures = 0;
let timer = 0;
let pumping = false;
const listeners = new Set<() => void>();
const eventListeners = new Set<(e: OutboxEvent) => void>();

const emit = (e: OutboxEvent) =>
  eventListeners.forEach((l) => {
    try {
      l(e);
    } catch {
      /* a screen's toast or reload failed: the item's outcome stands */
    }
  });
const publicOf = ({ key, orderId, number, kind, state, error, errorCode, queuedAt }: Entry): OutboxItem => ({ key, orderId, number, kind, state, error, errorCode, queuedAt });
const storedOf = (e: Entry): Stored => ({ key: e.key, userId: e.userId, savedAt: e.savedAt, itemKey: e.itemKey, orderId: e.orderId, number: e.number, kind: e.kind, request: e.request, hadCode: e.hadCode, recorded: e.recorded, draftKeys: e.draftKeys });
function changed() {
  snapshot = entries.map(publicOf);
  listeners.forEach((l) => l());
  guardUnload(entries.some((e) => e.state === 'waiting' || e.state === 'sending'));
}

let unloadGuarded = false;
const onBeforeUnload = (e: BeforeUnloadEvent) => {
  e.preventDefault();
  e.returnValue = '';
};
function guardUnload(on: boolean) {
  if (on === unloadGuarded) return;
  unloadGuarded = on;
  if (on) window.addEventListener('beforeunload', onBeforeUnload);
  else window.removeEventListener('beforeunload', onBeforeUnload);
}

const onOnline = () => schedule(RESEND_AFTER_ONLINE_MS);
function schedule(ms: number) {
  window.clearTimeout(timer);
  if (user === null || paused) return;
  timer = window.setTimeout(() => void pump(), ms);
}

/** The first item that may go now: waiting, with nothing of the same order (refused, held, in flight) before it. */
function next(): Entry | undefined {
  const blocked = new Set<number>();
  for (const e of entries) {
    if (e.state === 'waiting' && !blocked.has(e.orderId)) return e;
    blocked.add(e.orderId);
  }
  return undefined;
}

/** A failure worth another try: no connection, a server error, too many requests, a session that ended. */
const transient = (err: unknown) => err instanceof ApiError && (err.code === ERROR_CODE.NETWORK || err.status === 0 || err.status === 401 || err.status === 429 || err.status >= 500);

async function pump() {
  if (pumping || user === null || paused) return;
  pumping = true;
  const me = user;
  try {
    for (let e = next(); e && user === me && !paused; e = next()) {
      e.state = 'sending';
      changed();
      try {
        await send(e.request.path, { ...e.request.body, ...(e.secret ?? {}) });
        failures = 0;
        settle(e);
      } catch (err) {
        if (user !== me) return;
        if (transient(err)) {
          e.state = 'waiting';
          changed();
          if (err instanceof ApiError && err.status === 401) {
            // the session ended: nothing goes until this user signs in again (setUser resumes)
            paused = true;
            return;
          }
          failures++;
          const wait = err instanceof ApiError && err.retryAfterS ? err.retryAfterS * 1000 : RESEND_BACKOFF_MS[Math.min(failures - 1, RESEND_BACKOFF_MS.length - 1)];
          schedule(wait);
          return;
        }
        failures = 0;
        // refused as out of state: the first send may have arrived with its answer lost
        if (e.recorded && err instanceof ApiError && (err.code === ERROR_CODE.ORDER_STATE || err.code === ERROR_CODE.STALE_VERSION)) {
          const r = e.recorded;
          const done = await api<{ status: string }>(r.path)
            .then((o) => r.statuses.includes(o.status))
            .catch(() => false);
          if (done) {
            settle(e);
            continue;
          }
        }
        e.state = 'refused';
        e.error = errorText(err);
        e.errorCode = err instanceof ApiError ? err.code : null;
        // a photo the server no longer holds (its quarantine expired): the drafts forget it, so the form asks for a new one
        if (e.errorCode === ERROR_CODE.HUB_UPLOAD_INVALID) forgetPhotos(e.draftKeys);
        changed();
        emit({ item: publicOf(e), outcome: 'refused' });
      }
    }
  } finally {
    pumping = false;
  }
}

const PHOTO_FIELDS = ['photoUploadId', 'ktpUploadId'] as const;
function forgetPhotos(keys: string[]) {
  for (const k of keys) {
    const d = sessionStore.get<Record<string, unknown> | null>(k, null);
    if (d && typeof d === 'object' && PHOTO_FIELDS.some((f) => f in d)) {
      for (const f of PHOTO_FIELDS) delete d[f];
      sessionStore.set(k, d);
    }
  }
}

function settle(e: Entry) {
  entries = entries.filter((x) => x !== e);
  void idbDelete(e.key);
  e.draftKeys.forEach((k) => sessionStore.remove(k));
  changed();
  emit({ item: publicOf(e), outcome: 'sent' });
}

export const outbox = {
  /**
   * The signed-in user (null: nobody). The same user again resumes a paused queue; another user (or none) clears what
   * is in memory and loads that user's own stored items (the code-less ones wait for their code).
   */
  async setUser(id: number | null) {
    if (id !== null && id === user) {
      if (paused) {
        paused = false;
        schedule(0);
      }
      return;
    }
    window.clearTimeout(timer);
    user = id;
    paused = false;
    entries = [];
    failures = 0;
    changed();
    if (id === null) return;
    await idbPurgeOlder(OUTBOX_KEEP_MS);
    const stored = await idbAllFor<Stored>(id);
    if (user !== id) return;
    entries = stored
      .sort((a, b) => a.savedAt - b.savedAt)
      .map((s) => ({ ...s, state: s.hadCode ? 'needsCode' : 'waiting', error: null, errorCode: null, queuedAt: s.savedAt, secret: null }));
    window.removeEventListener('online', onOnline);
    window.addEventListener('online', onOnline);
    changed();
    schedule(0);
  },
  /** The session ended by itself (401 elsewhere): hold everything until the same user is back. */
  pause() {
    paused = true;
    window.clearTimeout(timer);
  },
  /** Signed out: memory and timer cleared, the unload guard off; the stored items stay for this user's next sign-in. */
  signOut() {
    void outbox.setUser(null);
  },
  /** Records a send that found no connection (or must wait behind another of its order). Same key: replaced, unless in flight. */
  enqueue(item: { key: string; orderId: number; number: string; kind: OutboxKind; request: OutboxRequest; recorded?: { path: string; statuses: string[] }; draftKeys?: string[] }) {
    if (user === null) return;
    if (entries.some((e) => e.key === `${user}:${item.key}` && e.state === 'sending')) return;
    const { confirmationCode, ...rest } = item.request.body as { confirmationCode?: string };
    const e: Entry = {
      key: `${user}:${item.key}`,
      userId: user,
      savedAt: Date.now(),
      itemKey: item.key,
      orderId: item.orderId,
      number: item.number,
      kind: item.kind,
      request: { path: item.request.path, body: rest },
      hadCode: typeof confirmationCode === 'string',
      recorded: item.recorded ?? null,
      draftKeys: item.draftKeys ?? [],
      state: 'waiting',
      error: null,
      errorCode: null,
      queuedAt: Date.now(),
      secret: typeof confirmationCode === 'string' ? { confirmationCode } : null,
    };
    entries = [...entries.filter((x) => x.key !== e.key), e];
    void idbPut(storedOf(e));
    window.removeEventListener('online', onOnline);
    window.addEventListener('online', onOnline);
    changed();
    schedule(RESEND_BACKOFF_MS[0]);
  },
  /** A restored exchange's code, entered again: it goes. */
  supplyCode(key: string, confirmationCode: string) {
    const e = entries.find((x) => x.key === key);
    if (!e || e.state !== 'needsCode') return;
    e.secret = { confirmationCode };
    e.state = 'waiting';
    changed();
    schedule(0);
  },
  /** Send what waits now (the "Kirim sekarang" button). */
  flush() {
    failures = 0;
    schedule(0);
  },
  /** Whether an order has something waiting (a new send for it must queue behind, never overtake it). */
  pendingFor: (orderId: number) => entries.some((e) => e.orderId === orderId && e.state !== 'refused'),
  /** The user has seen a refusal (it opens the order to fix it): the item is dropped, later ones of the order go. */
  dismiss(key: string) {
    entries = entries.filter((e) => e.key !== key);
    void idbDelete(key);
    changed();
    schedule(0);
  },
  items: () => snapshot,
  subscribe(l: () => void) {
    listeners.add(l);
    return () => void listeners.delete(l);
  },
  /** Every sent or refused item, once (the pages toast them). */
  onEvent(l: (e: OutboxEvent) => void) {
    eventListeners.add(l);
    return () => void eventListeners.delete(l);
  },
};

/** The items waiting, held for their code or refused, live. */
export const useOutbox = () => useSyncExternalStore(outbox.subscribe, outbox.items, outbox.items);
/** The items of one order, live. */
export function useOrderOutbox(orderId: number) {
  return useOutbox().filter((i) => i.orderId === orderId);
}
